import { getCurrencyMinorUnits } from "@redotech/money/currencies";
import {
  Currency,
  minorUnitsToAmount,
} from "@redotech/money/iso4217/currencies";
import { PaymentProviderType } from "@redotech/redo-billing-common/billing-engine-types";
import { BillingPeriod } from "@redotech/redo-billing-common/service-invoice";
import { BaseSubscriptionBillingSettings } from "@redotech/redo-billing-common/team-billing";
import { groupInput, input, InputSchema } from "@redotech/ui/form";
import { areDatesEqual } from "@redotech/util/date";

export const subscriptionBillingForm = groupInput(
  {
    enabled: input<boolean>(),
    paymentProvider: input<PaymentProviderType>(),
    renewOnPeriodEnd: input<boolean>(),
    startDate: input<Date | null>({ equal: areDatesEqual }),
    period: input<BillingPeriod>(),
    periodFeeInDollars: input<number>(),
    currency: input<Currency>(),
    isStartDateInFuture: input<boolean>(),
  },
  {
    validator: (value) => {
      if (value.enabled && !value.startDate) {
        return ["Start date is required when billing is enabled"];
      }
      if (!value.isStartDateInFuture) {
        return ["Start date must not have already passed"];
      }
      return [];
    },
  },
);

export type SubscriptionBillingForm = InputSchema.Input<
  typeof subscriptionBillingForm
>;

export type SubscriptionBillingValue = InputSchema.Value<
  typeof subscriptionBillingForm
>;

export const subscriptionBillingDefault: SubscriptionBillingValue = {
  enabled: false,
  renewOnPeriodEnd: false,
  startDate: null,
  period: BillingPeriod.MONTHLY,
  periodFeeInDollars: 0,
  currency: Currency.USD,
  paymentProvider: PaymentProviderType.SHOPIFY,
  isStartDateInFuture: true,
};

export interface SubscriptionBillingDefaultOverrides {
  paymentProvider?: PaymentProviderType;
  periodFeeInDollars?: number;
}

export function subscriptionBillingSettingsToFormValue(
  existingSubscriptionBilling: BaseSubscriptionBillingSettings | undefined,
  defaultOverrides: SubscriptionBillingDefaultOverrides = {},
): SubscriptionBillingValue {
  const defaults: SubscriptionBillingValue = {
    ...subscriptionBillingDefault,
    ...defaultOverrides,
  };

  return existingSubscriptionBilling
    ? {
        enabled: existingSubscriptionBilling.enabled,
        renewOnPeriodEnd: existingSubscriptionBilling.renewOnPeriodEnd,
        startDate: existingSubscriptionBilling.startDate
          ? new Date(existingSubscriptionBilling.startDate)
          : null,
        period: existingSubscriptionBilling.period,
        periodFeeInDollars: minorUnitsToAmount(
          existingSubscriptionBilling.periodFeeInCents,
          existingSubscriptionBilling.currency ?? Currency.USD,
        ),
        currency: existingSubscriptionBilling.currency ?? Currency.USD,
        paymentProvider:
          existingSubscriptionBilling.paymentProvider ??
          defaults.paymentProvider,
        isStartDateInFuture: true,
      }
    : defaults;
}

export function subscriptionBillingFormValueToTeam(
  value: SubscriptionBillingValue,
  /**
   * What is stored today. This form edits the fee and the schedule; the
   * merchant's collection terms are set elsewhere and are carried across
   * untouched. Both callers write the whole subdocument back, and the
   * subschema defaults the terms, so leaving them out would not omit them — it
   * would reset a merchant on net terms to charge-on-create.
   */
  current?: BaseSubscriptionBillingSettings | null,
) {
  return {
    enabled: value.enabled,
    renewOnPeriodEnd: value.renewOnPeriodEnd,
    startDate: value.startDate,
    period: value.period,
    periodFeeInCents: Math.round(
      value.periodFeeInDollars * 10 ** getCurrencyMinorUnits(value.currency),
    ),
    currency: value.currency,
    paymentProvider: value.paymentProvider,
    collectionMethod: current?.collectionMethod ?? undefined,
    netTermsDays: current?.netTermsDays ?? undefined,
  };
}
